import type { ComponentType, ReactElement } from 'react';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { AnalyticsMetric, InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import type { MegaGridColumn } from '@/design/MegaGrid/MegaGrid';

/**
 * Per-surface gallery (L0) layout. Drives a deliberately distinct presentation
 * for each surface so the galleries don't all look like the same hero-card grid:
 * an optional KPI stat band, an optional featured lead (a hero card or a real
 * inline visualization of one topic), then the remaining cards in a grid or list.
 */
export interface SurfaceGalleryLayout {
    statTiles?: AnalyticsMetric[];
    lead: 'hero-card' | 'inline-viz' | 'none';
    featuredTopicId?: string;
    cardStyle: 'standard' | 'compact';
    columns: { base: number; sm?: number; md?: number; lg?: number; xl?: number };
    /** When true, lead with the cross-category workspace digest (the AI Explorer overview). */
    digest?: boolean;
    /**
     * When true, the landing page renders the surface's signal-tile dashboard
     * (from `SurfaceSignalCatalog`) instead of the catalog drill-in gallery. The
     * `lead`/`statTiles`/`featuredTopicId` fields are then unused, but the topic
     * (L1) and evidence (L2) drill-in routes remain reachable.
     */
    signals?: boolean;
}

/**
 * A self-contained analytics experience: a route base, header copy, a catalog of
 * topics, and a gallery layout. The surface drill-in engine (gallery → topic →
 * evidence) is parameterized by surface, so adding one is a data change.
 */
export interface AnalyticsSurface {
    id: string;
    basePath: string;
    title: string;
    subtitle: string;
    catalog: TopicDefinition[];
    gallery: SurfaceGalleryLayout;
    /** Opt this surface into the reusable AI assist (inline search bar + ambient filters). */
    assist?: boolean;
    /** Surface-specific inspiration prompts for the assist bar; falls back to the generic set when absent. */
    assistInspiration?: string[];
    /** Model-facing sentence(s) describing this surface's analytical thesis and preferred fields — sent as assist context so answers stay scoped to the page. */
    assistScope?: string;
    /**
     * Feature-supplied landing-page body rendered instead of the gallery/signal
     * bodies (e.g. governance's policy register). Keeps the engine feature-blind:
     * the owning feature passes its layout in with its surface definition.
     */
    registerBody?: ComponentType<{ surface: AnalyticsSurface }>;
    /** Feature-supplied card appended after the gallery's topic cards (e.g. the classification builder card). */
    trailingCard?: ReactElement;
    /**
     * Drill-in presentation. `side-rail` renders topic pages with an accent
     * header band and the summary/insights in a right rail (the Behavior
     * treatment); `classic` (default) keeps the full-width stat band + callouts.
     */
    drillStyle?: 'classic' | 'side-rail';
    /**
     * Overrides the drill-in evidence table columns (a module-level constant),
     * e.g. Spend's token-consumption cut instead of the default classification
     * columns. Falls back to the standard evidence columns when absent.
     */
    evidenceColumns?: MegaGridColumn<InteractionRecord>[];
    /**
     * Framing of the drill-in evidence page. `classification` (default) shows
     * confidence stats, the provenance card, and the difficulty breakdown;
     * `consumption` shows token/cost stats and drops the classification rail —
     * the token spend view.
     */
    evidenceStyle?: 'classification' | 'consumption';
}

export function findTopicInSurface(surface: AnalyticsSurface, topicId: string | undefined): TopicDefinition | undefined {
    if (topicId === undefined) return undefined;
    return surface.catalog.find((topic) => topic.id === topicId);
}
